import { useMemo, useState } from 'react'
import {
    ScoopText,
    ScoopTable,
    ScoopButton,
    ScoopAccordion,
    ScoopAlert,
} from '../../../../../../ui'
import { zBandColor } from '../../../../utils/indicatorColors'
import styles from './PeerEvidence.module.scss'

const INITIAL_ROW_CAP = 10

// Peer statistics from the dossier's peerEvidence map (audit F4): keys are
// "metricLabel|lens", values carry entityValue, peer distribution, zScore,
// percentileRank, peerN, peerEntityIds, peerConfigHash.

const formatNumber = (value) => {
    if (value == null || Number.isNaN(Number(value))) return '—'
    const num = Number(value)
    if (Math.abs(num) >= 1000) {
        return num.toLocaleString('en-US', { maximumFractionDigits: 0 })
    }
    return num.toLocaleString('en-US', { maximumFractionDigits: 2 })
}

// English ordinals — "21st percentile", never "21th" (review D11).
const ordinalSuffix = (n) => {
    const mod100 = n % 100
    if (mod100 >= 11 && mod100 <= 13) return 'th'
    switch (n % 10) {
        case 1:
            return 'st'
        case 2:
            return 'nd'
        case 3:
            return 'rd'
        default:
            return 'th'
    }
}

const formatPercentile = (value) => {
    if (value == null || Number.isNaN(Number(value))) return '—'
    const p = Math.round(Number(value) * 100)
    return `${p}${ordinalSuffix(p)}`
}

export const buildPeerRows = (peerEvidence) => {
    if (!peerEvidence || typeof peerEvidence !== 'object') return []
    return Object.entries(peerEvidence)
        .map(([key, item], index) => ({
            _rowId: index,
            metric: item.metricLabel || key.split('|')[0],
            lens: item.lens || key.split('|')[1] || '—',
            entityValue: item.entityValue,
            peerMedian: item.peerMedian,
            zScore: item.zScore,
            percentileRank: item.percentileRank,
            peerN: item.peerN,
            peerEntityIds: item.peerEntityIds,
        }))
        .sort((a, b) => Math.abs(b.zScore || 0) - Math.abs(a.zScore || 0))
}

/**
 * Lenses that resolve to identical peer sets add no information — a known
 * degenerate configuration (a broader lens that resolves to the same peers as
 * the local one).
 * Surfacing it here is how an operator notices without reading raw dossiers.
 */
export const findDegenerateLensPairs = (peerEvidence) => {
    if (!peerEvidence || typeof peerEvidence !== 'object') return []
    const byLens = new Map()
    for (const item of Object.values(peerEvidence)) {
        if (!item?.lens || !Array.isArray(item.peerEntityIds)) continue
        const key = [...item.peerEntityIds].sort().join(',')
        if (!byLens.has(item.lens)) byLens.set(item.lens, key)
    }
    const lenses = [...byLens.entries()]
    const pairs = []
    for (let i = 0; i < lenses.length; i += 1) {
        for (let j = i + 1; j < lenses.length; j += 1) {
            if (lenses[i][1] === lenses[j][1] && lenses[i][1] !== '') {
                pairs.push([lenses[i][0], lenses[j][0]])
            }
        }
    }
    return pairs
}

const COLUMNS = [
    { field: 'metric', header: 'Metric', flex: 2, minWidth: 160 },
    { field: 'lens', header: 'Lens', width: 90 },
    {
        field: 'entityValue',
        header: 'Value',
        width: 110,
        valueGetter: ({ row }) => formatNumber(row.entityValue),
    },
    {
        field: 'peerMedian',
        header: 'Peer median',
        width: 110,
        valueGetter: ({ row }) => formatNumber(row.peerMedian),
    },
    {
        field: 'zScore',
        header: 'z',
        width: 90,
        render: ({ row }) => (
            // z is a score, not a state — colored bold text, not a chip (D2/D3)
            <span className={`${styles.zValue} ${styles[`z${zBandColor(row.zScore)}`]}`}>
                {formatNumber(row.zScore)}
            </span>
        ),
    },
    {
        field: 'percentileRank',
        header: 'Percentile',
        width: 100,
        valueGetter: ({ row }) => formatPercentile(row.percentileRank),
    },
    {
        field: 'peerN',
        header: 'Peers',
        width: 80,
        valueGetter: ({ row }) => (row.peerN != null ? `n=${row.peerN}` : '—'),
    },
]

export const PeerEvidence = ({ peerEvidence }) => {
    const [showAll, setShowAll] = useState(false)
    const rows = useMemo(() => buildPeerRows(peerEvidence), [peerEvidence])
    const degeneratePairs = useMemo(() => findDegenerateLensPairs(peerEvidence), [peerEvidence])

    if (rows.length === 0) return null

    const visibleRows = showAll ? rows : rows.slice(0, INITIAL_ROW_CAP)

    return (
        <div className={styles.peerEvidence}>
            <ScoopAccordion className={styles.accordion}>
                <ScoopAccordion.Summary>
                    <div className={styles.summaryRow}>
                        <ScoopText className={styles.sectionTitle}>
                            Peer Comparison ({rows.length})
                        </ScoopText>
                    </div>
                </ScoopAccordion.Summary>
                <ScoopAccordion.Details>
                    {/* One warning, one place, as an alert (review D11) — the
                        duplicated chip + contrast-failing note are gone. */}
                    {degeneratePairs.length > 0 && (
                        <ScoopAlert severity="warning" className={styles.degenerateAlert}>
                            {degeneratePairs
                                .map(
                                    ([a, b]) =>
                                        `The "${a}" and "${b}" lenses resolve to the same peer set — the second lens adds no signal.`
                                )
                                .join(' ')}
                        </ScoopAlert>
                    )}
                    <ScoopTable
                        columns={COLUMNS}
                        rows={visibleRows}
                        domLayout="autoHeight"
                        rowHeight={32}
                        headerHeight={40}
                    />
                    {!showAll && rows.length > INITIAL_ROW_CAP && (
                        <ScoopButton
                            variant="outlineGray"
                            size="xs"
                            onClick={() => setShowAll(true)}
                            className={styles.showAllButton}
                        >
                            Show all {rows.length} comparisons
                        </ScoopButton>
                    )}
                </ScoopAccordion.Details>
            </ScoopAccordion>
        </div>
    )
}
